import { type OpenApiDataType, dataFrom } from '@zyno-io/openapi-client-codegen';
import { formatError } from '@zyno-io/vue-foundation';
import { type Dictionary, keyBy } from 'lodash';
import { type Ref, computed, ref } from 'vue';

import { AuthAccountsApi, AuthMspApi, AuthRolesApi } from '@/openapi-clients-generated/auth';
import {
    SalesInventoryCategoriesApi,
    SalesInventoryVendorsApi,
    SalesSalesConfigCustomPaymentMethodsApi,
    SalesSalesConfigPaymentTerminalsApi,
    SalesSalesConfigProductCategoriesApi,
    SalesSalesConfigTaxesApi,
    SalesSalesFinancialAccountsApi,
    SalesSalesFulfillmentBoxTypesApi
} from '@/openapi-clients-generated/sales';
import {
    SuiteCoreLocationHoursApi,
    SuiteCoreLocationsApi,
    SuiteCoreTagsApi,
    SuiteCrmConfigContactRelationshipTypesApi,
    SuiteCrmConfigContactTypesApi,
    SuiteFormsApi
} from '@/openapi-clients-generated/suite';
import {
    TalkAccessApi,
    TalkAiAgentsApi,
    TalkCallerRoutesApi,
    TalkCallFlowConfigsApi,
    TalkCarrierTelnyxEmergencyAddressesApi,
    TalkEndpointConfigsApi,
    TalkExtensionsApi,
    TalkNumbersApi,
    TalkParkingLotsApi,
    TalkRingGroupsApi,
    TalkSubscribedNumbersApi,
    TalkTenantApi,
    TalkTenantOverviewApi,
    TalkTtsRenderApi,
    TalkTimeConditionsApi,
    TalkVoicemailApi,
    TalkWorkstationExtensionLinksApi,
    type ITenantResponse
} from '@/openapi-clients-generated/talk';

// Talk requests fire from non-Talk surfaces too (e.g. the QAB phone icon in the app shell). For
// tenants that aren't provisioned for Talk, GET /tenant 404s — so the talk* loaders below are gated
// on DataCache.talkProvisioningGate (wired by global.ts to GlobalState.ensureTalkProvisioned),
// skipping the request and leaving the cache empty when Talk is unavailable for the active tenant.
function talkLoader<T>(loader: () => Promise<T>): () => Promise<T> {
    return async () => {
        if (!DataCache.talkProvisioningGate || (await DataCache.talkProvisioningGate())) return loader();
        // eslint-disable-next-line no-explicit-any
        return { data: undefined } as any;
    };
}

const CacheLoaders = {
    authRoles: () => AuthRolesApi.getRolesList(),
    authAccounts: () => AuthAccountsApi.getAccountsList(),
    // Current tenant members, for pickers. `authAccounts` is admin-only; any member may call lookup.
    authAccountLookup: () => AuthAccountsApi.getAccountsLookup({ query: { membership: 'current' } }),
    coreLocations: () => SuiteCoreLocationsApi.getCoreLocationsIndex(),
    coreLocationHours: () => SuiteCoreLocationHoursApi.getCoreLocationHoursIndex(),
    crmTags: () => SuiteCoreTagsApi.getCoreTagsIndex({ path: { module: 'crm' } }),
    crmContactTypes: () => SuiteCrmConfigContactTypesApi.getCrmConfigContactTypesIndex(),
    crmContactRelationshipTypes: () => SuiteCrmConfigContactRelationshipTypesApi.getCrmConfigContactRelationshipTypesIndex(),
    forms: () => SuiteFormsApi.getFormsListForms({}),
    inventoryCategories: () => SalesInventoryCategoriesApi.getInventoryCategoriesIndex(),
    inventoryVendors: () => SalesInventoryVendorsApi.getInventoryVendorsIndex(),
    salesCategories: () => SalesSalesConfigProductCategoriesApi.getSalesConfigProductCategoriesIndex(),
    salesCustomPaymentMethods: () => SalesSalesConfigCustomPaymentMethodsApi.getSalesConfigCustomPaymentMethodsIndex(),
    salesPaymentTerminals: () => SalesSalesConfigPaymentTerminalsApi.getSalesConfigPaymentTerminalsIndex(),
    salesFulfillmentBoxTypes: () => SalesSalesFulfillmentBoxTypesApi.getSalesFulfillmentBoxTypesList(),
    salesFulfillmentPredefinedBoxTypes: () => SalesSalesFulfillmentBoxTypesApi.getSalesFulfillmentBoxTypesListPredefined(),
    salesTaxes: () => SalesSalesConfigTaxesApi.getSalesConfigTaxesIndex(),
    salesFinancialAccounts: () => SalesSalesFinancialAccountsApi.getSalesFinancialAccountsIndex(),
    // Unlike the other talk loaders, a skipped load has to stay distinguishable from a pending one:
    // consumers gate rendering on "the voice backend is not known yet". So this resolves an explicit
    // null for a tenant without Talk, leaving `undefined` to mean "still loading".
    talkTenant: async (): Promise<{ data: ITenantResponse | null }> => {
        if (DataCache.talkProvisioningGate && !(await DataCache.talkProvisioningGate())) return { data: null };
        return { data: dataFrom(await TalkTenantApi.getTenantGetTenant()) };
    },
    // Post-provisioning setup still in flight (number ports/orders, hardware). Only meaningful while
    // the tenant's `onboardingStatus` is 'active'; the service answers with an empty object otherwise.
    talkOverview: talkLoader(() => TalkTenantOverviewApi.getTenantOverviewGet()),
    talkAccess: talkLoader(() => TalkAccessApi.getAccessGetComposite()),
    talkCallFlows: talkLoader(() => TalkCallFlowConfigsApi.getCallFlowConfigsListConfigs()),
    // Tenant-admin only, and read from call history as well as the Caller Rules screen — cached so
    // both agree on who is blocked. Only fetched behind that admin gate (see useCallerBlocking).
    talkCallerRoutes: talkLoader(() => TalkCallerRoutesApi.getCallerRoutesList()),
    talkNumbers: talkLoader(() => TalkNumbersApi.getNumbersList()),
    talkExtensions: talkLoader(() => TalkExtensionsApi.getExtensionsListExtensions()),
    talkEndpointConfigurations: talkLoader(() => TalkEndpointConfigsApi.getEndpointConfigsListConfigurations()),
    // A location only counts as e911-capable once it carries a carrier emergency address, so this —
    // not the Core location list — is what "e911 enabled location" means. Cached because four
    // surfaces ask (number editor, extension editor, overview, location editor) and they must agree.
    talkEmergencyAddresses: talkLoader(() => TalkCarrierTelnyxEmergencyAddressesApi.getCarrierTelnyxEmergencyAddressesList()),
    talkSubscribedNumbers: talkLoader(() => TalkSubscribedNumbersApi.getSubscribedNumbersIndex()),
    talkRingGroups: talkLoader(() => TalkRingGroupsApi.getRingGroupsListGroups()),
    talkParkingLots: talkLoader(() => TalkParkingLotsApi.getParkingLotsListLots()),
    talkAiAgents: talkLoader(() => TalkAiAgentsApi.getAiAgentsListAgents()),
    talkAiAgentCapabilities: talkLoader(() => TalkAiAgentsApi.getAiAgentsGetCapabilities()),
    talkTimeConditions: talkLoader(() => TalkTimeConditionsApi.getTimeConditionsListConditions()),
    talkTtsCatalog: talkLoader(() => TalkTtsRenderApi.getTtsRenderGetCatalog()),
    talkVoicemailCounts: talkLoader(() => TalkVoicemailApi.getVoicemailGetCounts()),
    // Any identity may call `/current`, workstation or not — gated separately (not via `talkLoader`)
    // on `GlobalState.isWorkstation` by `useTalkWorkstationExtension`, so a browser session never
    // makes this request at all.
    talkWorkstationExtension: () => TalkWorkstationExtensionLinksApi.getWorkstationExtensionLinksCurrent(),
    mspTenants: () => AuthMspApi.getMspGetMspTenants()
};

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

export type ICacheKey = keyof typeof CacheLoaders;

export type CacheDataType<T extends ICacheKey> = OpenApiDataType<Awaited<ReturnType<(typeof CacheLoaders)[T]>>>;

type TypeWithId = {
    // eslint-disable-next-line no-explicit-any
    [K in ICacheKey]: CacheDataType<K> extends any[] ? (CacheDataType<K>[number] extends { id: any } ? K : never) : never;
}[ICacheKey];

export interface AsyncRef<T> extends Ref<T> {
    load?: () => Promise<void>;
    reload?: () => Promise<void>;
    backgroundReload?: () => Promise<void>;
    loadError?: string;
    loadPromise?: Promise<void>;
}
export function asyncRef<T>(value: T): AsyncRef<T>;
export function asyncRef<T>(): AsyncRef<T | undefined>;
export function asyncRef<T>(value?: T): AsyncRef<T | undefined> {
    return ref(value) as AsyncRef<T>;
}

export class DataCache {
    static refCache: { [K in ICacheKey]?: AsyncRef<CacheDataType<K> | undefined> } = {};

    // Wired by global.ts. Resolves false when the active tenant isn't provisioned for Talk; the
    // talk* loaders skip their request in that case (see talkLoader).
    static talkProvisioningGate?: () => Promise<boolean>;

    static waitFor<T extends ICacheKey>(key: T): Promise<void> {
        if (!this.refCache[key]) {
            return this.getRef(key).loadPromise!;
        }
        if (this.refCache[key]?.loadPromise) {
            return this.refCache[key]!.loadPromise;
        }
        return Promise.resolve();
    }

    static getLazyRef<T extends ICacheKey>(key: T): AsyncRef<CacheDataType<T> | undefined> {
        if (!this.refCache[key]) {
            const newRef = ref<CacheDataType<T> | undefined>() as AsyncRef<CacheDataType<T> | undefined>;
            // `loadError` must be reactive: consumers read it via `computed(() => ref.loadError)` or
            // directly in templates to render an error state. A plain property on the ref object is
            // NOT tracked by Vue (only `.value` is), and an initial-load failure never touches
            // `.value` — so the error would silently never render. Back it with a ref via accessors.
            const loadErrorRef = ref<string | undefined>();
            Object.defineProperty(newRef, 'loadError', {
                get: () => loadErrorRef.value,
                set: (v: string | undefined) => {
                    loadErrorRef.value = v;
                },
                enumerable: true,
                configurable: true
            });
            const load = () => {
                return CacheLoaders[key]().then(
                    result => {
                        // eslint-disable-next-line no-explicit-any
                        newRef.value = dataFrom(result as any) as CacheDataType<T>;
                    },
                    err => {
                        newRef.loadError = formatError(err);
                    }
                );
            };
            newRef.load = () => {
                newRef.loadPromise ??= load();
                return newRef.loadPromise;
            };
            newRef.reload = () => {
                newRef.value = undefined;
                newRef.loadError = undefined;
                return (newRef.loadPromise = load());
            };
            newRef.backgroundReload = () => {
                newRef.loadError = undefined;
                return (newRef.loadPromise = load());
            };
            // eslint-disable-next-line no-explicit-any
            this.refCache[key] = newRef as any; // ts issue
        }
        return this.refCache[key]!;
    }

    static getRef<T extends ICacheKey>(key: T): AsyncRef<CacheDataType<T> | undefined> {
        const ref = this.getLazyRef(key);
        if (!ref.loadPromise) ref.reload?.();
        return ref;
    }

    static getMapById<T extends TypeWithId>(key: T) {
        const ref = this.getRef(key);
        return computed(() => keyBy(ref.value, 'id') as Dictionary<CacheDataType<T>[number]>);
    }

    static reloadCaches() {
        for (const [_key, ref] of Object.entries(this.refCache)) {
            ref.reload?.();
        }
    }

    static reloadCache<T extends ICacheKey>(key: T, background = false) {
        if (this.refCache[key]) {
            this.refCache[key][background ? 'backgroundReload' : 'reload']?.();
        }
    }
}
